import { useState, useEffect, useCallback } from 'react';
// V82.7 Phase 3.1: Firestore SDK direct read 사용 제거됨 — 모두 callable 경유.
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { ApprovalRequest, ApprovalRequestType, ApprovalRequestStatus } from '../types/approvalRequest';
import { useCurrentUser } from './useCurrentUser';

/**
 * V156: getMyApprovalRequests CF 요청 타입
 */
interface GetMyApprovalRequestsInput {
  status?: ApprovalRequestStatus;
  includeCanceled?: boolean;
  limit?: number;
  // V156 확장
  year?: number;
  type?: ApprovalRequestType;
  cursor?: string;
  pageSize?: number;
  // V243: 검색
  query?: string;
  page?: number;
}

/**
 * V156: getMyApprovalRequests CF 응답 타입
 */
interface GetMyApprovalRequestsResponse {
  success: boolean;
  items: ApprovalRequest[];
  total: number;
  // V156 확장
  hasMore?: boolean;
  nextCursor?: string;
}

/**
 * V156: useMyApprovalRequests 옵션
 */
interface UseMyApprovalRequestsOptions {
  status?: ApprovalRequestStatus;
  year?: number;
  type?: ApprovalRequestType;
  pageSize?: number;
  query?: string;  // V243: 검색어
}

/**
 * 내가 요청한 승인 요청 목록 조회
 *
 * V155.1: Firestore 직접 조회 → Cloud Function 경유로 전환
 * V156: year, type 필터 및 페이지네이션 지원
 * - 보안 강화: requesterId는 서버에서 auth.uid로 고정
 * - Firestore Rules 의존 제거
 */
export function useMyApprovalRequests(options?: UseMyApprovalRequestsOptions) {
  const { user } = useCurrentUser();
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // V156: 페이지네이션 상태
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [total, setTotal] = useState(0);  // V243: 전체(또는 검색) 결과 수

  const { status, year, type, pageSize, query } = options || {};

  // V243: cursor 또는 page 기반 조회
  const fetchPage = useCallback(async (cursor?: string, page?: number) => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const functions = getFunctions();
      const getMyApprovalRequests = httpsCallable<
        GetMyApprovalRequestsInput,
        GetMyApprovalRequestsResponse
      >(functions, 'getMyApprovalRequests');

      const result = await getMyApprovalRequests({
        status,
        includeCanceled: true,
        year,
        type,
        cursor: cursor || undefined,
        pageSize: pageSize || 20,
        query: query?.trim() || undefined,
        page: page || undefined,
      });

      if (result.data.success) {
        setRequests(result.data.items);
        setHasMore(result.data.hasMore || false);
        setNextCursor(result.data.nextCursor);
        setTotal(result.data.total || result.data.items.length);
      } else {
        throw new Error('승인 요청 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('승인 요청 목록 조회 실패:', err);
      const errorMessage = err.message || '승인 요청 목록 조회 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [user?.profile?.id, status, year, type, pageSize, query]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return {
    requests,
    isLoading,
    error,
    refetch: () => fetchPage(),
    // V237.1: cursor 기반 페이지네이션
    hasMore,
    nextCursor,
    fetchPage,
    total,  // V243
  };
}

/**
 * V157: getApprovalsForApprover CF 요청 타입
 */
interface GetApprovalsForApproverInput {
  status?: ApprovalRequestStatus;
  year?: number;
  type?: ApprovalRequestType;
  cursor?: string;
  pageSize?: number;
}

/**
 * V157: getApprovalsForApprover CF 응답 타입
 */
interface GetApprovalsForApproverResponse {
  success: boolean;
  items: ApprovalRequest[];
  total: number;
  hasMore?: boolean;
  nextCursor?: string;
}

/**
 * V157: useApprovalsForApprover 옵션
 */
interface UseApprovalsForApproverOptions {
  status?: ApprovalRequestStatus;
  year?: number;
  type?: ApprovalRequestType;
  pageSize?: number;
}

/**
 * 내가 승인해야 하는 요청 목록 조회
 *
 * V157: Firestore 직접 조회 → Cloud Function 경유로 전환
 * - 보안 강화: approverId는 서버에서 auth.uid로 고정
 * - Firestore Rules 의존 제거
 * - year, type 필터 및 페이지네이션 지원
 */
export function usePendingApprovals(options?: UseApprovalsForApproverOptions | ApprovalRequestStatus) {
  const { user } = useCurrentUser();
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);  // V315: loadMore 전용 로딩
  const [error, setError] = useState<Error | null>(null);
  // V157: 페이지네이션 상태
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

  // 하위 호환: string으로 받으면 status로 처리
  const opts: UseApprovalsForApproverOptions = typeof options === 'string'
    ? { status: options }
    : options || {};

  const { status, year, type, pageSize } = opts;

  const refetch = useCallback(async (cursor?: string, append = false) => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }

    try {
      // V315: append(loadMore)일 때는 isLoadingMore만 활성화
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // V157: Cloud Function 호출
      const functions = getFunctions();
      const getApprovalsForApprover = httpsCallable<
        GetApprovalsForApproverInput,
        GetApprovalsForApproverResponse
      >(functions, 'getApprovalsForApprover');

      const result = await getApprovalsForApprover({
        status,
        year,
        type,
        cursor,
        pageSize: pageSize || 20,
      });

      if (result.data.success) {
        if (append) {
          setRequests((prev) => [...prev, ...result.data.items]);
        } else {
          setRequests(result.data.items);
        }
        // V157: 페이지네이션 상태 업데이트
        setHasMore(result.data.hasMore || false);
        setNextCursor(result.data.nextCursor);
      } else {
        throw new Error('승인 요청 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('승인 대기 목록 조회 실패:', err);
      const errorMessage = err.message || '승인 요청 목록 조회 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);  // V315
    }
  }, [user?.profile?.id, status, year, type, pageSize]);

  // V157: 다음 페이지 로드
  const loadMore = useCallback(() => {
    if (hasMore && nextCursor) {
      refetch(nextCursor, true);
    }
  }, [hasMore, nextCursor, refetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    requests,
    isLoading,
    isLoadingMore,  // V315
    error,
    refetch: () => refetch(),
    // V157 추가
    hasMore,
    loadMore,
  };
}

/**
 * V64: 승인자 대시보드용 pending 건수 조회 (페이지네이션 무관)
 *
 * 목적: WorkMyDashboardPage 등 대시보드 카드에서 "전체 승인대기 건수"를 표시.
 * 기존 usePendingApprovals는 pageSize에 종속된 items 배열만 반환하여
 * `requests.length`를 count로 쓰면 첫 페이지 크기로 캡되는 문제가 있음.
 *
 * - BE: getPendingApprovalCountForApprover (Firestore count() aggregation)
 * - 필터: companyCode + approverId(auth.uid) + status='pending'
 */
export function usePendingApprovalCount() {
  const { user } = useCurrentUser();
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const functions = getFunctions();
      const fn = httpsCallable<{}, { success: boolean; count: number }>(
        functions,
        'getPendingApprovalCountForApprover'
      );
      const result = await fn({});
      if (result.data.success) {
        setCount(result.data.count || 0);
      } else {
        throw new Error('승인대기 건수 조회 실패');
      }
    } catch (err: any) {
      console.error('승인대기 건수 조회 실패:', err);
      setError(new Error(err.message || '승인대기 건수 조회 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  }, [user?.profile?.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { count, isLoading, error, refetch };
}

/**
 * 특정 승인 요청 상세 조회
 *
 * V82.7 Phase 3.1: Firestore direct getDoc → callable `getApprovalRequestById` 전환.
 *   - 권한 검증 (본인/승인자/HR) 은 BE accessPolicy + 자체 검증
 *   - 응답 shape 동일
 */
export function useApprovalRequestById(requestId?: string) {
  const [request, setRequest] = useState<ApprovalRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!requestId) {
      setIsLoading(false);
      return;
    }

    const fetchRequest = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const fn = httpsCallable<
          { requestId: string },
          { success: boolean; request: ApprovalRequest | null }
        >(getFunctions(), 'getApprovalRequestById');
        const r = await fn({ requestId });
        setRequest(r.data.request);
      } catch (err) {
        console.error('승인 요청 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequest();
  }, [requestId]);

  return { request, isLoading, error };
}

/**
 * 승인 요청 통계
 *
 * V82.7 Phase 3.1: Firestore direct getDocs → callable `getApprovalStats` 전환.
 *   - BE 가 본인을 approverId 로 카운트 (기존 로직 동일)
 */
export function useApprovalStats() {
  const { user } = useCurrentUser();
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const fn = httpsCallable<
          {},
          { success: boolean; stats: { pending: number; approved: number; rejected: number } }
        >(getFunctions(), 'getApprovalStats');
        const r = await fn({});
        setStats(r.data.stats);
      } catch (err) {
        console.error('승인 통계 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [user?.profile?.id]);

  return { stats, isLoading, error };
}
